// Versions of project files, read from the file name alone. Every version is its
// own file in SharePoint (nothing is ever overwritten); this module decides which
// files are versions of the same document and what the next version is called.
//
// One module for gv-crm-mcp (Deno) and the project page (Vite), so both group and
// number files the same way. Pure: no imports, no I/O.
//
// Three ways a version shows up in a name, checked in this order:
//   gv       GV_<locatie>_<type>_v<major>.<minor>[.<update>][_CONCEPT]_<taal>.<ext>   (ADR 0015, 0017)
//            v3.1.1 is a small update of v3.1; it sorts between v3.1 and v3.2
//   offerte  ... P018.2 ...   the quote number, '.2' is the revision
//   suffix   ..._v2... / ... V3...   a v-number as its own word
// Anything else is version 1 of itself; its next version gets '_v2'.

export type VersionScheme = "gv" | "offerte" | "suffix" | "none";

/**
 * How far the next version goes when no number is given (GV convention):
 * 'small' 3.1 → 3.1.1 → 3.1.2 (the default), 'large' 3.1.2 → 3.2.
 */
export type VersionStep = "small" | "large";

export interface ParsedFileName {
  name: string;
  scheme: VersionScheme;
  /** Same for every version, language and format of one document (lower case). */
  documentKey: string;
  /** Display name of the document, without version, language or extension. */
  title: string;
  /** Version as written: '0.2' or '3.1.1' (gv), '2' (offerte, suffix), '1' when the name carries none. */
  version: string;
  /** Comparable version: [major, minor, update] for gv (update 0 when the name has two parts), [n] otherwise. */
  rank: number[];
  concept: boolean;
  /** Language from the GV convention ('NL', 'EN'); null otherwise. */
  lang: string | null;
  /** Extension as written. */
  ext: string;
}

export class FileVersionError extends Error {
  override name = "FileVersionError";
}

const GV_RE =
  /^GV_(?<stem>[\p{L}\p{N}][\p{L}\p{N}_-]*?)_v(?<version>(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)(?:\.(?:0|[1-9]\d*))?)(?<concept>_CONCEPT)?_(?<lang>[A-Z]{2})\.(?<ext>[A-Za-z0-9]{1,10})$/u;
const OFFERTE_RE = /^(?<pre>.*?\bP\d{2,3})\.(?<n>[1-9]\d?)(?<post>(?:\s*[-_ ].*)?)\.(?<ext>[A-Za-z0-9]{1,10})$/u;
const SUFFIX_RE = /^(?<pre>.+)(?<sep>[ _-])(?<v>[vV])(?<n>[1-9]\d{0,2})(?<post>(?:[ _-][^.]*)?)\.(?<ext>[A-Za-z0-9]{1,10})$/u;
const PLAIN_RE = /^(?<stem>.+)\.(?<ext>[A-Za-z0-9]{1,10})$/u;

const GV_VERSION_RE = /^(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)(?:\.(?:0|[1-9]\d*))?$/;
const N_VERSION_RE = /^[1-9]\d{0,2}$/;

const key = (s: string) => s.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
const tidy = (s: string) => s.replace(/\s+/g, " ").trim();

/** Internal: everything needed to rebuild a name with another version. */
interface Parts {
  parsed: ParsedFileName;
  rebuild(version: string, concept: boolean): string;
}

function parts(raw: string): Parts | null {
  const name = raw.normalize("NFC");

  const gv = GV_RE.exec(name)?.groups;
  if (gv) {
    // 3.1 and 3.1.0 are the same version: a missing third part is 0.
    const [major, minor, update = 0] = gv.version!.split(".").map(Number) as [number, number, number?];
    return {
      parsed: {
        name,
        scheme: "gv",
        documentKey: `gv:${key(gv.stem!)}`,
        title: tidy(gv.stem!.replace(/_/g, " ")),
        version: gv.version!,
        rank: [major, minor, update],
        concept: gv.concept !== undefined,
        lang: gv.lang!,
        ext: gv.ext!,
      },
      rebuild: (v, concept) => `GV_${gv.stem}_v${v}${concept ? "_CONCEPT" : ""}_${gv.lang}.${gv.ext}`,
    };
  }

  const of = OFFERTE_RE.exec(name)?.groups;
  if (of) {
    return {
      parsed: {
        name,
        scheme: "offerte",
        documentKey: `offerte:${key(of.pre!)}`,
        title: tidy(`${of.pre}${of.post}`),
        version: of.n!,
        rank: [Number(of.n)],
        concept: false,
        lang: null,
        ext: of.ext!,
      },
      rebuild: (v) => `${of.pre}.${v}${of.post}.${of.ext}`,
    };
  }

  const sf = SUFFIX_RE.exec(name)?.groups;
  if (sf) {
    return {
      parsed: {
        name,
        scheme: "suffix",
        documentKey: `n:${key(`${sf.pre}${sf.post}`)}`,
        title: tidy(`${sf.pre}${sf.post}`),
        version: sf.n!,
        rank: [Number(sf.n)],
        concept: false,
        lang: null,
        ext: sf.ext!,
      },
      rebuild: (v) => `${sf.pre}${sf.sep}${sf.v}${v}${sf.post}.${sf.ext}`,
    };
  }

  const pl = PLAIN_RE.exec(name)?.groups;
  if (pl) {
    return {
      parsed: {
        name,
        scheme: "none",
        documentKey: `n:${key(pl.stem!)}`,
        title: tidy(pl.stem!),
        version: "1",
        rank: [1],
        concept: false,
        lang: null,
        ext: pl.ext!,
      },
      rebuild: (v) => `${pl.stem}_v${v}.${pl.ext}`,
    };
  }
  return null;
}

/** Reads the version from a file name; null for a name without extension. */
export function parseFileName(name: string): ParsedFileName | null {
  return parts(name)?.parsed ?? null;
}

/** Negative when a is older than b, 0 when equal, positive when newer. */
export function compareRank(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** Same document and same variant (language and format): one version chain. */
export function sameChain(a: ParsedFileName, b: ParsedFileName): boolean {
  return a.documentKey === b.documentKey && a.lang === b.lang && a.ext.toLowerCase() === b.ext.toLowerCase();
}

/** GV convention: 3.1 → 3.1.1 and 3.1.4 → 3.1.5 (small), 3.1.4 → 3.2 (large). */
function bumpGv(rank: number[], step: VersionStep): string {
  const [major, minor, update] = rank as [number, number, number];
  return step === "large" ? `${major}.${minor + 1}` : `${major}.${minor}.${update + 1}`;
}

/**
 * In the GV convention: the version a small update belongs to ('3.1' for 3.1.2);
 * null for 3.1 itself and for every other name.
 */
export function baseVersionOf(p: Pick<ParsedFileName, "scheme" | "rank">): string | null {
  return p.scheme === "gv" && (p.rank[2] ?? 0) > 0 ? `${p.rank[0]}.${p.rank[1]}` : null;
}

/**
 * The name of the next version of `previousName`. Without `version` the step
 * decides (gv): a small update by default (3.1 → 3.1.1), a large one on request
 * (3.1.1 → 3.2). Other names carry one number, which goes up by one whatever
 * the step. `concept` exists only in the GV convention and defaults to that of
 * the previous version. The new version must be higher than the previous one.
 */
export function nextVersionName(
  previousName: string,
  opts: { version?: string; step?: VersionStep; concept?: boolean } = {},
): { name: string; parsed: ParsedFileName } {
  const p = parts(previousName);
  if (!p) throw new FileVersionError(`'${previousName}' heeft geen extensie; daar kan geen versie van gemaakt worden.`);
  const prev = p.parsed;
  if (opts.version !== undefined && opts.step !== undefined) {
    throw new FileVersionError("Geef een versienummer of een stap (klein of groot) op, niet allebei.");
  }

  let version: string;
  if (prev.scheme === "gv") {
    version = opts.version === undefined ? bumpGv(prev.rank, opts.step ?? "small") : opts.version.trim().replace(/^v/i, "");
    if (!GV_VERSION_RE.test(version)) {
      throw new FileVersionError(
        `Versie '${opts.version}' heeft niet het formaat <major>.<minor> of <major>.<minor>.<update> (bijv. 0.3, 2.10 of 3.1.2).`,
      );
    }
  } else {
    if (opts.concept === true) {
      throw new FileVersionError(`CONCEPT bestaat alleen in de GV-naamconventie; '${prev.name}' volgt die niet.`);
    }
    version = opts.version === undefined ? String(prev.rank[0]! + 1) : opts.version.trim().replace(/^v/i, "");
    if (!N_VERSION_RE.test(version)) throw new FileVersionError(`Versie '${opts.version}' moet een heel getal zijn (bijv. 2).`);
  }

  const name = p.rebuild(version, prev.scheme === "gv" ? opts.concept ?? prev.concept : false);
  const parsed = parseFileName(name);
  if (!parsed || !sameChain(parsed, prev)) {
    throw new FileVersionError(`Kon geen nieuwe versienaam maken voor '${prev.name}' (uitkomst '${name}').`);
  }
  if (compareRank(parsed.rank, prev.rank) <= 0) {
    throw new FileVersionError(`Versie ${version} is niet hoger dan de vorige (${prev.version}).`);
  }
  return { name, parsed };
}

/**
 * Checks a caller-chosen name for the next version of `previousName`: same
 * document, same language and format, higher version.
 */
export function assertNextVersion(previousName: string, nextName: string): ParsedFileName {
  const prev = parseFileName(previousName);
  const next = parseFileName(nextName);
  if (!prev || !next) throw new FileVersionError("Beide bestandsnamen moeten een extensie hebben.");
  if (!sameChain(prev, next)) {
    throw new FileVersionError(`'${nextName}' is geen versie van '${previousName}' (ander document, andere taal of ander formaat).`);
  }
  if (compareRank(next.rank, prev.rank) <= 0) {
    throw new FileVersionError(`Versie ${next.version} in '${nextName}' is niet hoger dan ${prev.version}.`);
  }
  return next;
}

/**
 * The newest existing version below `name` in one folder, when `name` is a
 * higher version of a document that is already there. Used to recognise an
 * upload that is really a new version.
 */
export function previousVersionIn(name: string, namesInFolder: string[]): ParsedFileName | null {
  const own = parseFileName(name);
  if (!own) return null;
  let best: ParsedFileName | null = null;
  for (const n of namesInFolder) {
    if (n.normalize("NFC") === own.name) continue;
    const p = parseFileName(n);
    if (!p || !sameChain(p, own) || compareRank(p.rank, own.rank) >= 0) continue;
    if (!best || compareRank(p.rank, best.rank) > 0) best = p;
  }
  return best;
}

/**
 * The newest version of the document `name` belongs to that is in the folder
 * (a file with this very name counts); null when the folder has none.
 */
export function newestVersionIn(name: string, namesInFolder: string[]): ParsedFileName | null {
  const own = parseFileName(name);
  if (!own) return null;
  let best: ParsedFileName | null = null;
  for (const n of namesInFolder) {
    const p = parseFileName(n);
    if (!p || !sameChain(p, own)) continue;
    if (!best || compareRank(p.rank, best.rank) > 0) best = p;
  }
  return best;
}

export interface VersionedFile {
  name: string;
  /** Folder relative to the project folder ('' = the project folder itself). */
  folder: string;
}

export interface DocumentVersion<T extends VersionedFile> {
  version: string;
  rank: number[];
  /** '3.1' when this is a small update of it (3.1.1, 3.1.2); null for 3.1 itself and outside the GV convention. */
  updateOf: string | null;
  concept: boolean;
  /** One file per variant (language or format), sorted NL first, then by name. */
  files: { file: T; parsed: ParsedFileName }[];
}

export interface DocumentGroup<T extends VersionedFile> {
  /** Unique within a project: folder plus document. */
  key: string;
  folder: string;
  title: string;
  scheme: VersionScheme;
  /** Newest first. */
  versions: DocumentVersion<T>[];
}

const LANG_ORDER = ["NL", "EN", "DE", "FR"];
const langRank = (l: string | null) => (l === null ? -1 : LANG_ORDER.indexOf(l) === -1 ? 99 : LANG_ORDER.indexOf(l));

/** Groups files into documents with their versions, newest version first. */
export function groupDocuments<T extends VersionedFile>(files: T[]): DocumentGroup<T>[] {
  const groups = new Map<string, DocumentGroup<T>>();
  for (const file of files) {
    const parsed = parseFileName(file.name);
    const docKey = `${file.folder}|${parsed ? parsed.documentKey : `raw:${key(file.name)}`}`;
    let g = groups.get(docKey);
    if (!g) {
      g = { key: docKey, folder: file.folder, title: parsed?.title ?? file.name, scheme: parsed?.scheme ?? "none", versions: [] };
      groups.set(docKey, g);
    }
    const p: ParsedFileName = parsed ?? { name: file.name, scheme: "none", documentKey: docKey, title: file.name, version: "1", rank: [1], concept: false, lang: null, ext: "" };
    let v = g.versions.find((x) => compareRank(x.rank, p.rank) === 0);
    if (!v) {
      v = { version: p.version, rank: p.rank, updateOf: baseVersionOf(p), concept: p.concept, files: [] };
      g.versions.push(v);
    }
    v.concept = v.concept || p.concept;
    v.files.push({ file, parsed: p });
  }
  for (const g of groups.values()) {
    g.versions.sort((a, b) => compareRank(b.rank, a.rank));
    for (const v of g.versions) {
      v.files.sort((a, b) => langRank(a.parsed.lang) - langRank(b.parsed.lang) || a.file.name.localeCompare(b.file.name, "nl"));
    }
    // The title follows the newest version (an offerte can be renamed between revisions).
    const newest = g.versions[0]?.files[0]?.parsed;
    if (newest) g.title = newest.title;
  }
  return [...groups.values()];
}
